import type { DeliveryFinalStateCode } from "@/features/delivery/utils/tracking-map-state";
import { Button } from "@/components/ui/button";

type Props = {
  finalState: DeliveryFinalStateCode;
  title: string;
  /** CTA copy. When undefined, no CTA is rendered. */
  ctaLabel?: string;
  /** Where the CTA navigates. When undefined, the CTA button is not rendered. */
  ctaHref?: string;
  /** Optional secondary click handler used when ctaHref is omitted (e.g. retry). */
  onCtaClick?: () => void;
  className?: string;
};

// =============================================================================
// Issue #82 — Final-state overlay for the Delivery Tracking map.
//
// A simple, accessible overlay rendered on top of the (still-visible) map
// when the Delivery reaches a terminal state. The map stays rendered so the
// route context (origin / destination / last Rider location) remains visible
// while the terminal copy is on screen.
//
// The CTA defaults to `Volver al pedido` and routes back to the existing
// Order status page; for the delivered state with an authenticated session
// the route passes `Calificar pedido` so the CTA lands on the same Order
// status page (which already owns the rating dialog).
// =============================================================================

export function DeliveryTrackingFinalStateOverlay({
  finalState,
  title,
  ctaLabel,
  ctaHref,
  onCtaClick,
  className,
}: Props) {
  const showCta = ctaLabel && (ctaHref || onCtaClick);
  return (
    <div
      data-testid="delivery-tracking-final-state"
      data-final-state={finalState}
      role="status"
      aria-live="polite"
      className={
        "pointer-events-auto absolute inset-x-3 top-3 rounded-lg border bg-background/95 p-4 text-center shadow-lg backdrop-blur " +
        (className ?? "")
      }
    >
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      {showCta ? (
        <div className="mt-3 flex justify-center">
          {ctaHref ? (
            <Button asChild type="button">
              <a href={ctaHref}>{ctaLabel}</a>
            </Button>
          ) : (
            <Button type="button" onClick={onCtaClick}>
              {ctaLabel}
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
}
